import React, { useState } from 'react';
import { Driver, Severity, ActivityStatus } from '../data/mockData';
import { ChevronRight, Search, Navigation, Moon, MapPin, Coffee } from 'lucide-react';
interface DriverListProps {
  drivers: Driver[];
  selectedDriverId: string | null;
  onSelectDriver: (driverId: string) => void;
  variant?: 'active' | 'inactive';
}
export function DriverList({
  drivers,
  selectedDriverId,
  onSelectDriver,
  variant = 'active'
}: DriverListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'driving' | 'on-site' | 'rest' | 'offline'>('all');
  // Compute filter counts
  const activityCounts = {
    all: drivers.length,
    driving: drivers.filter(d => d.activityStatus === 'driving').length,
    'on-site': drivers.filter(d => d.activityStatus === 'on-site').length,
    rest: drivers.filter(d => d.activityStatus === 'rest').length,
    offline: drivers.filter(d => d.signalBars === 0).length
  };
  const filteredDrivers = drivers.filter(d => {
    const matchesSearch = d.name.toLowerCase().includes(searchQuery.toLowerCase()) || d.truckId.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (filter === 'all') return true;
    if (filter === 'offline') return d.signalBars === 0;
    return d.activityStatus === filter;
  });
  const sortedDrivers = [...filteredDrivers].sort((a, b) => {
    const priority: Record<Severity, number> = {
      critical: 0,
      warning: 1,
      offline: 2,
      info: 3,
      normal: 4
    };
    return priority[a.status] - priority[b.status];
  });
  return <div className="flex flex-col h-full" data-id="element-335">
      {/* Search & Filter Header */}
      <div className="p-3 border-b border-slate-800 space-y-3 shrink-0" data-id="element-336">
        {variant === 'active' && <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800" data-id="element-337">
            <FilterTab active={filter === 'all'} onClick={() => setFilter('all')} label="All" count={activityCounts.all} data-id="element-338" />
            <FilterTab active={filter === 'driving'} onClick={() => setFilter('driving')} label="Driving" count={activityCounts.driving} data-id="element-339" />
            <FilterTab active={filter === 'on-site'} onClick={() => setFilter('on-site')} label="On Site" count={activityCounts['on-site']} data-id="element-340" />
            <FilterTab active={filter === 'rest'} onClick={() => setFilter('rest')} label="Rest" count={activityCounts.rest} data-id="element-341" />
            <FilterTab active={filter === 'offline'} onClick={() => setFilter('offline')} label="Offline" count={activityCounts.offline} data-id="element-342" />
          </div>}
        <div className="relative" data-id="element-343">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-344" />
          <input type="text" placeholder="Search drivers or trucks..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-slate-600 transition-colors" data-id="element-345" />
        </div>
      </div>

      {/* Driver List */}
      <div className="flex-1 overflow-y-auto p-3 scrollbar-hide" data-id="element-346">
        {sortedDrivers.length === 0 && <div className="text-center p-8 text-slate-500 text-sm" data-id="element-347">
            No drivers match your search.
          </div>}
        {sortedDrivers.map(driver => {
        const isSelected = driver.id === selectedDriverId;
        const hosPercent = driver.hosDriven / 11 * 100;
        const nextStop = driver.route.find(s => s.status !== 'completed');
        const completedStops = driver.route.filter(s => s.status === 'completed').length;
        const totalStops = driver.route.length;
        const severityConfig = variant === 'inactive' ? {
          dot: 'bg-slate-500'
        } : getSeverityConfig(driver.status);
        const activityConfig = variant === 'inactive' ? {
          color: 'text-slate-500',
          label: 'SLEEPER',
          icon: <Moon className="w-3 h-3" data-id="element-348" />
        } : getActivityConfig(driver.activityStatus);
        const isOffline = driver.signalBars === 0;
        return <div key={driver.id} onClick={() => onSelectDriver(driver.id)} className={`relative p-3 mb-2 last:mb-0 rounded-xl cursor-pointer transition-all
                ${isSelected ? 'bg-slate-800/60 border border-slate-600 shadow-[0_0_15px_rgba(0,0,0,0.2)]' : 'bg-slate-900/50 border border-slate-800 hover:border-slate-700'}`} data-id="element-349">
              {isSelected && <div className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-full ${variant === 'inactive' ? 'bg-slate-500' : driver.status === 'critical' ? 'bg-brand-red' : driver.status === 'warning' ? 'bg-brand-amber' : 'bg-brand-green'}`} data-id="element-350" />}

              {/* Row 1: Truck ID (primary) + Activity Status */}
              <div className="flex items-center justify-between mb-1" data-id="element-351">
                <div className="flex items-center gap-2" data-id="element-352">
                  <div className={`w-2.5 h-2.5 rounded-full ${severityConfig.dot}`} data-id="element-353">
                    {driver.status === 'critical' && <span className="absolute w-2.5 h-2.5 rounded-full bg-brand-red animate-ping opacity-50" data-id="element-354" />}
                  </div>
                  <span className={`text-sm font-bold font-mono ${isSelected ? 'text-slate-100' : 'text-slate-200'}`} data-id="element-355">
                    {driver.truckId}
                  </span>
                  {isOffline ? <span className="inline-flex items-center gap-1" style={{
                background: '#8EA0B6',
                borderRadius: '2px',
                padding: '1px 4px'
              }} data-id="element-356">
                      <img src="https://cdn.magicpatterns.com/uploads/qtiWE7RgqX6uR9cCddEQgD/android_cell_4_bar_off_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24.svg" alt="No signal" className="w-3 h-3" style={{
                  filter: 'brightness(0.2)'
                }} data-id="element-357" />
                      <span className="text-[10px] font-bold text-slate-900 whitespace-nowrap" data-id="element-358">
                        Offline
                      </span>
                      <span className="font-mono font-medium uppercase text-slate-900/60 whitespace-nowrap ml-1 text-[9px]" data-id="element-359">
                        {driver.lastPing}
                      </span>
                    </span> : <SignalBars bars={driver.signalBars ?? 4} data-id="element-360" />}
                </div>
                <span className={`text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1 ${activityConfig.color}`} data-id="element-361">
                  {activityConfig.icon}
                  {activityConfig.label}
                </span>
              </div>

              {/* Row 2: Driver name */}
              <div className="flex items-center justify-between mb-2 ml-[18px]" data-id="element-362">
                <span className="text-xs text-slate-400 truncate" data-id="element-363">
                  {driver.name}
                </span>
              </div>

              {/* Row 3: Next stop + ETA + Stop count */}
              <div className="flex items-center justify-between ml-[18px]" data-id="element-364">
                <span className="text-[10px] text-slate-500 truncate" data-id="element-365">
                  {nextStop ? <>
                      Next: {nextStop.address}, Richmond, VA{' '}
                      <span className="text-slate-600" data-id="element-366">•</span>{' '}
                      <span className="font-mono uppercase" data-id="element-367">
                        ETA {nextStop.eta}
                      </span>
                    </> : 'All stops completed'}
                </span>
                <div className="flex items-center gap-1.5 shrink-0 ml-2" data-id="element-368">
                  <span className="text-[10px] text-slate-500" data-id="element-369">
                    Stop {completedStops}/{totalStops}
                  </span>
                  <ChevronRight className={`w-3.5 h-3.5 ${isSelected ? 'text-slate-400' : 'text-slate-600'}`} data-id="element-370" />
                </div>
              </div>
            </div>;
      })}
      </div>
    </div>;
}
function FilterTab({
  active,
  onClick,
  label,
  count
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
}) {
  return <button onClick={onClick} className={`flex-1 text-[10px] py-1.5 rounded-md transition-colors font-medium flex items-center justify-center gap-1.5
        ${active ? 'bg-slate-800 text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-371">
      {label}
      {count !== undefined && <span className={`min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${active ? 'bg-slate-700 text-slate-300' : 'bg-slate-800 text-slate-500'}`} data-id="element-372">
          {count}
        </span>}
    </button>;
}
function getSeverityConfig(status: Severity) {
  switch (status) {
    case 'critical':
      return {
        dot: 'bg-brand-red'
      };
    case 'warning':
      return {
        dot: 'bg-brand-amber'
      };
    case 'offline':
      return {
        dot: 'bg-slate-500'
      };
    case 'info':
      return {
        dot: 'bg-brand-blue'
      };
    default:
      return {
        dot: 'bg-brand-green'
      };
  }
}
function getActivityConfig(activity: ActivityStatus) {
  switch (activity) {
    case 'driving':
      return {
        color: 'text-slate-200',
        label: 'DRIVING',
        icon: <Navigation className="w-3 h-3" data-id="element-373" />
      };
    case 'sleeper':
      return {
        color: 'text-slate-500',
        label: 'SLEEPER',
        icon: <Moon className="w-3 h-3" data-id="element-374" />
      };
    case 'rest':
      return {
        color: 'text-slate-200',
        label: 'REST',
        icon: <Coffee className="w-3 h-3" data-id="element-375" />
      };
    case 'on-site':
      return {
        color: 'text-slate-200',
        label: 'ON SITE',
        icon: <MapPin className="w-3 h-3" data-id="element-376" />
      };
    case 'breakdown':
      return {
        color: 'text-slate-500',
        label: 'DOWN',
        icon: <Moon className="w-3 h-3" data-id="element-377" />
      };
  }
}
function SignalBars({
  bars
}: {
  bars: number;
}) {
  if (bars === 0) {
    return <img src="https://cdn.magicpatterns.com/uploads/qtiWE7RgqX6uR9cCddEQgD/android_cell_4_bar_off_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24.svg" alt="No signal" className="w-3.5 h-3.5 opacity-50" data-id="element-378" />;
  }
  const heights = [3, 5.5, 8, 10.5];
  return <svg width="12" height="12" viewBox="0 0 14 14" className="shrink-0" data-id="element-379">
      {heights.map((h, i) => <rect key={i} x={i * 3.5} y={14 - h} width="2.5" height={h} rx="0.5" fill={i < bars ? '#94a3b8' : '#334155'} data-id="element-380" />)}
    </svg>;
}